import mapboxgl from 'mapbox-gl';
import { geojson, linestring } from '../map.constants';
import { TRANSPARENT_COLOR } from '../Utils/colors';
import {
  ENTITY_LAYER_NAMES,
  MAP_LAYER_IDS,
  MAP_SOURCE_IDS,
  MAP_TILESET_TYPES,
  MAPBOX_LAYERS,
  WELLIGENCE_SOURCE,
} from '../Utils/constants';
import {
  addDynamicLayers,
  addRulerContext,
  buildHighlightLayerId,
  buildLabelsLayerId,
  layerNeedsLabels,
} from '../Utils/layers';
import { MAP_LAYER_TYPES } from '../Utils/layerTypes';
import mapStyles, {
  getPropertyDefault,
  getPropertyHighlight,
  getStyleOptions,
} from '../Utils/mapStyles';
import { createClickedFeatureForSearch, rulerEvent, toggleMapRuler } from '../Utils/utils';

const VALID_ENTITY_SOURCES = [
  WELLIGENCE_SOURCE,
  MAP_SOURCE_IDS.eaWells,
  MAP_SOURCE_IDS.lngFacilities,
  MAP_SOURCE_IDS.pipelineNetworks,
  MAP_SOURCE_IDS.screening,
  MAP_SOURCE_IDS.composite,
  MAP_SOURCE_IDS.co2Emitters,
  MAP_SOURCE_IDS.ccusProjects,
  MAP_SOURCE_IDS.storageSites,
];

const SOURCE_TO_LAYER = {
  [MAPBOX_LAYERS.countryBoundaries]: ENTITY_LAYER_NAMES.countries,
  [MAPBOX_LAYERS.customRegions]: ENTITY_LAYER_NAMES.customRegions,
};

const addOnCountryClick = (map, layerClickCallback) => {
  map.on('click', e => {
    const features = map.queryRenderedFeatures(e.point, {
      layers: [MAP_LAYER_IDS.countryFillBoundaries, MAP_LAYER_IDS.customRegionFillBoundaries],
    });
    if (features.length > 0) {
      // Prevent event from bubbling to other click handlers
      e.preventDefault();

      const displayFeature = features[0];
      const clickedFeature = { ...displayFeature.properties };
      const layerCountry = displayFeature.properties.iso_3166_1_alpha_3;
      const layerName = SOURCE_TO_LAYER[displayFeature.sourceLayer] || ENTITY_LAYER_NAMES.countries;

      if (clickedFeature && layerCountry) {
        layerClickCallback({
          properties: clickedFeature,
          sourceLayer: layerName,
          layerCountry: layerCountry,
          coordinates: e.lngLat,
        });
      }
    }
  });
};

const addOnEntityClick = (map, layerClickCallback) => {
  map.on('click', e => {
    const features = map.queryRenderedFeatures(e.point);
    if (features && features.length > 0) {
      const feature = features[0];
      const layerCountry = feature?.properties?.countryIsoCode;
      // Only call layerClickCallback if we have a valid entity feature
      // Skip background layers, country boundaries, and other non-entity layers
      // Check if the clicked feature is from a valid entity source
      if (
        feature &&
        VALID_ENTITY_SOURCES.includes(feature.source) &&
        feature.properties &&
        feature.properties.entityType
      ) {
        layerClickCallback({
          properties: feature.properties,
          sourceLayer: feature.sourceLayer,
          layerCountry: layerCountry,
          coordinates: e.lngLat,
        });
      }
    }
  });
};

// Get layer visibility for all layers
const getLayersVisibility = (map, layers) => {
  return layers.reduce((visibility, layerName) => {
    if (map.getLayer(layerName)) {
      if (layerNeedsLabels(layerName)) {
        const labelLayerId = buildLabelsLayerId(layerName);
        visibility[labelLayerId] = map.getLayoutProperty(labelLayerId, 'visibility');
      }
      visibility[layerName] = map.getLayoutProperty(layerName, 'visibility');
    }

    return visibility;
  }, {});
};

// Set the visibility of layers based on the provided visibility object
const setLayersVisibility = (map, visibility) => {
  const layers = Object.keys(visibility);
  for (const layerName of layers) {
    if (visibility[layerName] !== undefined && map.getLayer(layerName)) {
      if (layerNeedsLabels(layerName)) {
        map.setLayoutProperty(buildLabelsLayerId(layerName), 'visibility', visibility[layerName]);
      }
      map.setLayoutProperty(layerName, 'visibility', visibility[layerName]);
    }
  }
};

const onLoad = ({
  map,
  style,
  ruler,
  hideLayers,
  initialMetric,
  initialSizeType,
  callbacks,
  layers,
  layerFilters,
  layerClickCallback,
  enableEntityHighlight,
  enableCountryClick,
  defaultDistanceMetric,
  source,
}) => {
  addDynamicLayers({
    map,
    hideLayers,
    initialMetric,
    initialSizeType,
    layers,
    enableEntityHighlight,
    source,
    style,
  });

  // Apply layer filters if provided
  if (layerFilters && layerFilters.length > 0) {
    map.layerCallbacks.filterLayers(layerFilters);
  }

  // Add country click functionality if enabled
  if (enableCountryClick) {
    addOnCountryClick(map, layerClickCallback);
  }

  // Add click handlers for layers
  if (layerClickCallback) {
    addOnEntityClick(map, layerClickCallback);
  }

  // Create ruler
  if (ruler) {
    addRulerContext(map, geojson, style);
    map.onClickRulerCallback = rulerEvent(map, geojson, linestring, defaultDistanceMetric);
  }

  // Add custom callbacks
  for (const callbackName of Object.keys(callbacks)) {
    map.on(callbackName, e => callbacks[callbackName](e, map));
  }

  // Add hover effects
  map.on('mousemove', e => {
    const features = map?.queryRenderedFeatures?.(e.point) || [];

    // Check if there's a valid clickable entity being hovered
    const hasClickableEntity = features.some(
      feature =>
        VALID_ENTITY_SOURCES.includes(feature.source) &&
        feature.properties &&
        feature.properties.entityType,
    );

    map.getCanvas().style.cursor = hasClickableEntity ? 'pointer' : 'grab';
  });
};

export const createDynamicMap = ({
  container,
  style,
  center,
  zoom,
  ruler,
  control,
  enableInfoPopup,
  enableCountryClick = false,
  layerClickCallback,
  layerFilters,
  layers,
  hideLayers,
  enableEntityHighlight,
  defaultDistanceMetric,
  callbacks = {},
  tilesetType = MAP_TILESET_TYPES.oilAndGasMap,
  initialMetric,
  initialSizeType,
  source,
}) => {
  const map = new mapboxgl.Map({
    container,
    style: mapStyles[style],
    center,
    zoom,
    attributionControl: false,
    maxPitch: 0,
    preserveDrawingBuffer: true,
    minZoom: 2,
  });

  // Define getTilesetType before any layer initialization
  Object.defineProperty(map, 'getTilesetType', {
    value: () => tilesetType,
    writable: false,
    configurable: true,
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();

  if (control) {
    const navControl = new mapboxgl.NavigationControl();
    map.addControl(navControl, 'bottom-right');
    map._navigationControl = navControl;
    const scaleControl = new mapboxgl.ScaleControl({ unit: defaultDistanceMetric });
    map.addControl(scaleControl, 'bottom-right');
    map._scaleControl = scaleControl;
  }

  if (enableInfoPopup) {
    map.infoPopup = new mapboxgl.Popup({ className: 'mapbox-popup' });
    map.setPopup = (infoHtml, coordinates) => {
      map.infoPopup.setLngLat(coordinates).setHTML(infoHtml).addTo(map);
    };
  }

  // Add country click functionality if enabled
  if (enableCountryClick) {
    addOnCountryClick(map, layerClickCallback);
  }

  map.layerCallbacks = {
    changeLayerStyle: (layer, metric, size) => {
      const styles = getStyleOptions(layer, metric, size);

      if (styles && map.getLayer(layer)) {
        for (const style of styles) {
          map.setPaintProperty(layer, style.property, style.value);
        }
      }
    },
    filterLayer: (layer, filter) => {
      if (map.getLayer(layer)) {
        map.setFilter(layer, filter);
      }
    },
    filterLayers: layerFilters => {
      for (const { entityTypes, filter } of layerFilters) {
        for (const entityType of entityTypes) {
          if (map.getLayer(entityType)) {
            map.setFilter(entityType, filter);
          }
        }
      }
    },
    setCountryBoundaryStyle: (
      style = {
        country: TRANSPARENT_COLOR,
        custom_region: TRANSPARENT_COLOR,
        selected_country: TRANSPARENT_COLOR,
        selected_custom_region: TRANSPARENT_COLOR,
      },
    ) => {
      map.setPaintProperty(MAP_LAYER_IDS.countryLineBoundaries, 'line-color', style.country);
      map.setPaintProperty(
        MAP_LAYER_IDS.customRegionLineBoundaries,
        'line-color',
        style.custom_region,
      );
      map.setPaintProperty(
        MAP_LAYER_IDS.countrySelectedBoundaries,
        'line-color',
        style.selected_country,
      );
      map.setPaintProperty(
        MAP_LAYER_IDS.customRegionSelectedBoundaries,
        'line-color',
        style.selected_custom_region,
      );
    },
    setHeatMapStyle: (
      style = {
        country: TRANSPARENT_COLOR,
        custom_region: TRANSPARENT_COLOR,
        region_label: 0,
      },
    ) => {
      map.setPaintProperty(MAP_LAYER_IDS.countryFillBoundaries, 'fill-color', style.country);
      map.setPaintProperty(
        MAP_LAYER_IDS.customRegionFillBoundaries,
        'fill-color',
        style.custom_region,
      );
      map.setPaintProperty(MAP_LAYER_IDS.customRegionLabels, 'text-opacity', style.region_label);
    },
    toggleLayer: (entityTypes, visibility) => {
      const layers = [...entityTypes];
      // If toggling the formations layer, also toggle the presalt polygons layer
      if (entityTypes.includes(MAP_LAYER_TYPES.formations)) {
        layers.push(MAP_LAYER_TYPES.presaltPolygons);
      }

      // If toggling the blocks layer, also toggle the leases layer
      if (entityTypes.includes(MAP_LAYER_TYPES.blocks)) {
        layers.push(MAP_LAYER_TYPES.leases);
      }

      // If toggling the basins layer, also toggle the basins labels layer
      // Special case for basins, because their labels naming convention is different (defined in Mapbox)
      if (entityTypes.includes(MAP_LAYER_TYPES.basins)) {
        layers.push(MAP_LAYER_IDS.basinsLabels);
      }

      // make sure layers are unique
      for (const layer of [...new Set(layers)]) {
        if (map.getLayer(layer)) {
          map.setFilter(layer, null); // Reset any existing filters (for child toggles, eg. "Exploration" Wells)
          map.setLayoutProperty(layer, 'visibility', visibility);
        }

        if (!layer.includes('labels')) {
          // Also toggle the label layer if it exists and it's not already included
          const labelLayerId = buildLabelsLayerId(layer);
          if (map.getLayer(labelLayerId)) {
            map.setFilter(labelLayerId, null);
            map.setLayoutProperty(labelLayerId, 'visibility', visibility);
          }
        }
      }
    },
  };

  // Add missing methods that Oil and Gas map expects
  map.getFeaturesFromMap = (
    e,
    displayProperties = ['type', 'properties', 'id', 'layer', 'source', 'sourceLayer', 'state'],
  ) => {
    const features = map.queryRenderedFeatures(e.point);
    return features.map(feature => {
      const displayFeature = {};
      for (const prop of displayProperties) {
        displayFeature[prop] = feature[prop];
      }
      return displayFeature;
    });
  };

  map.createClickedFeatureForSearch = (layerName, value) =>
    createClickedFeatureForSearch(layerName, value);

  map.resetZoom = () => {
    map.flyTo({
      center: [0, 0],
      zoom: 1.5,
    });
  };

  map.toggleMapRuler = rulerActive => toggleMapRuler(map, rulerActive);

  map.toggleMapStyle = newStyle => {
    // Store current map state
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    const visibility = getLayersVisibility(map, layers);

    // Set the new style
    map.setStyle(mapStyles[newStyle]);

    // Reinitialize the map after style change
    map.once('style.load', () => {
      onLoad({
        map,
        style: newStyle,
        ruler,
        callbacks,
        hideLayers,
        initialMetric,
        initialSizeType,
        layers,
        layerFilters,
        layerClickCallback,
        enableEntityHighlight,
        enableCountryClick,
        source,
      });

      // Restore center and zoom
      map.setCenter(currentCenter);
      map.setZoom(currentZoom);
      // Restore layer visibility
      setLayersVisibility(map, visibility);

      // Restore any layer filters
      for (const [layerId, filter] of Object.entries(map.savedFilters)) {
        if (map.getLayer(layerId) && filter) {
          map.setFilter(layerId, filter);
        }
      }
      // Restore any layer paint properties
      for (const [layerName, properties] of Object.entries(map.savedPaintProperties)) {
        if (layerName && properties) {
          map.setPaintProperty(layerName, ...properties);
        }
      }
    });
  };

  map.addLayerHighlight = (currentItem, prevItemRef) => {
    if (currentItem?.sourceLayer && currentItem.properties) {
      const layerName = currentItem.sourceLayer;

      if (map.getLayer(layerName)) {
        const highlight = getPropertyHighlight(layerName, currentItem.properties);

        if (highlight?.screening) {
          // Blocks, Assets, Leases
          const { widthProperty, widthValue } = highlight.screening;

          try {
            map.savedPaintProperties = { [MAP_LAYER_IDS.screening]: [widthProperty, widthValue] };
            map.setPaintProperty(MAP_LAYER_IDS.screening, widthProperty, widthValue);
          } catch (_e) {
            map.savedPaintProperties = {
              [MAP_LAYER_IDS.screening]: ['fill-outline-color', widthValue],
            };
            // TODO can we use this line instead of the above two?
            map.setPaintProperty(MAP_LAYER_IDS.screening, 'fill-outline-color', widthValue);
          }
        }

        // Highlight the layer using the property and value from the highlight object, if present:
        if (highlight?.property && highlight?.value) {
          // Prioritize the layer name from the highlight object, if available:
          const layerToHighlight = highlight.layer || layerName;
          map.savedPaintProperties = { [layerToHighlight]: [highlight.property, highlight.value] };
          map.setPaintProperty(layerToHighlight, highlight.property, highlight.value);
        }

        // Highlight the layer using the highlight layer, if present:
        const layerId = buildHighlightLayerId(layerName);
        const highlightLayer = map.getLayer(layerId);
        if (highlightLayer) {
          const filter = [
            'all',
            ['==', 'legacyId', currentItem.properties.legacyId],
            ['==', 'countryIsoCode', currentItem.properties.countryIsoCode],
          ];
          map.savedFilters = { ...map.savedFilters, [layerId]: filter };
          map.setFilter(layerId, filter);
        }
      }
    }
    prevItemRef.current = currentItem;
  };

  map.removeLayerHighlight = prevItem => {
    if (prevItem?.sourceLayer) {
      const layerName = prevItem.sourceLayer;

      if (map.getLayer(layerName)) {
        const defaultStyle = getPropertyDefault(layerName, prevItem.properties);

        if (defaultStyle?.screening) {
          // Blocks, Assets, Leases
          const { colorProperty, colorValue, widthProperty, widthValue } = defaultStyle.screening;
          try {
            delete map.savedPaintProperties[MAP_LAYER_IDS.screening];
            map.setPaintProperty(MAP_LAYER_IDS.screening, colorProperty, colorValue);
            map.setPaintProperty(MAP_LAYER_IDS.screening, widthProperty, widthValue);
          } catch (_e) {
            delete map.savedPaintProperties[MAP_LAYER_IDS.screening];
            // TODO can we use this line instead of the above two?
            map.setPaintProperty(MAP_LAYER_IDS.screening, 'fill-outline-color', colorValue);
          }
        }

        // Reset the default style on the previous layer:
        if (defaultStyle?.property && defaultStyle?.value) {
          // Prioritize the layer name from the default style, if available:
          const prevLayerName = defaultStyle.layer || layerName;
          delete map.savedPaintProperties[prevLayerName];
          map.setPaintProperty(prevLayerName, defaultStyle.property, defaultStyle.value);
        }

        // Reset the filter on the previous highlight layer:
        const prevHighlightLayerId = buildHighlightLayerId(layerName);
        if (map.getLayer(prevHighlightLayerId)) {
          const filter = ['all', ['==', 'legacyId', ''], ['==', 'countryIsoCode', '']];
          map.savedFilters = { ...map.savedFilters, [prevHighlightLayerId]: filter };
          map.setFilter(prevHighlightLayerId, filter);
        }
      }
    }
  };

  // Initialize the map with dynamic layers
  map.on('load', () =>
    onLoad({
      map,
      style,
      ruler,
      callbacks,
      hideLayers,
      initialMetric,
      initialSizeType,
      layers,
      layerFilters,
      layerClickCallback,
      enableEntityHighlight,
      enableCountryClick,
      defaultDistanceMetric,
      source,
    }),
  );

  return map;
};
